import * as Sentry from '@sentry/react';
import { useEffect, type FC } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import type {
  MyUserDetailsResponse,
  OrganisationDetailsResponse,
  OrganisationServiceRoot,
} from 'src/apis/organisation-service/types';
import { Button } from 'src/components/buttons-and-actions/button';
import { OptToggle } from 'src/components/domain-specifics/opt-toggle';
import { Form } from 'src/components/form/form';
import { formGapClasses } from 'src/components/form/form-container';
import { Textarea } from 'src/components/form/textarea';
import { requiredOutput } from 'src/components/form/zod-utilities';
import { NewDesignToggleDev } from 'src/components/layout/app-shell/new-design-toggle-dev';
import { ExternalLink } from 'src/components/navigation/link';
import { Modal } from 'src/components/overlay/modal';
import { useAxios } from 'src/hooks/use-axios';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import type { ModalApi } from 'src/hooks/use-modal';
import { useModal2 } from 'src/hooks/use-modal';
import { useNow } from 'src/hooks/use-now';
import { useSafeLocalStorage } from 'src/hooks/use-storage';
import { useStorybook } from 'src/hooks/use-storybook';
import {
  toggleNewDesign,
  useIsNewDesignActive,
} from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';
import { handleError } from 'src/utils/handle-error';
import { locationReload } from 'src/utils/location-usage';
import { reportToSentry } from 'src/utils/report-to-sentry';

export const NewDesignToggle: FC = () => {
  const { handleToggleRequests, isNewDesignActive } = useToggleDesign();

  const feedbackModal = useModal2({
    modal: (props) => <LeaveFeedbackModal {...props} />,
    trigger: (props) => (
      <Button mode="link" className="legacy:underline" {...props}>
        leave feedback
      </Button>
    ),
  });

  const optOutModal = useModal2({
    modal: (props) => <OptOutModal {...props} />,
    trigger: () => null,
  });

  return (
    <div
      data-testid="new-design-toggle"
      className={`
        grid items-center justify-between gap-md bg-background-surface-subdued
        px-2xl py-lg
        md:grid-cols-[1fr_auto]
        print:hidden
      `}
    >
      <div>
        <span>
          Use the new PRISMA Platform design - clearer workflow, better
          experience. Read the{' '}
          <ExternalLink
            className="legacy:underline"
            href="https://www.prisma-capacity.eu/news/insights/a-fresh-look-for-the-prisma-platform"
            target="_blank"
          >
            summary of all changes
          </ExternalLink>
          . Please {feedbackModal.trigger}.
        </span>
        {feedbackModal.modal}
        {optOutModal.modal}
      </div>

      <div className="flex items-center gap-md">
        {(PRISMA_CONFIG.stage === 'local' || PRISMA_CONFIG.stage === 'ent') && (
          <NewDesignToggleDev />
        )}

        <OptToggle
          label="New Design"
          isChecked={isNewDesignActive}
          onChange={() => {
            if (isNewDesignActive) {
              optOutModal.open();
            } else {
              handleToggleRequests.execute({
                updateToggle: true,
                feedback: null,
              });
            }
          }}
          tooltip="You can always switch back to the old design as long as the test phase is live. Please help us to improve and send us feedback to our Customer Success Team."
          loading={handleToggleRequests.pending}
        />
      </div>
    </div>
  );
};

const leaveFeedbackSchema = z.object({
  feedback: z.string().nullable().transform(requiredOutput()),
});

const LeaveFeedbackModal: FC<ModalApi> = ({ close, closeButton }) => {
  const { handleToggleRequests } = useToggleDesign();
  const user = useCognitoUser();

  useEffect(() => {
    if (!handleToggleRequests.response) return;
    toast.success('Thank you for providing your feedback.');
    close();
    // oxlint-disable-next-line exhaustive-deps
  }, [handleToggleRequests.response]);

  return (
    <Modal
      title="New Design Feedback"
      onDismiss={close}
      size="lg"
      confirmButton={null}
      closeButton={null}
    >
      <Form
        schema={leaveFeedbackSchema}
        initialValues={{ feedback: null }}
        onSubmit={(values) =>
          handleToggleRequests.execute({
            updateToggle: false,
            feedback: values.feedback,
          })
        }
        constraintViolation={null}
        submitButton={
          <Button type="submit" disabled={handleToggleRequests.pending}>
            {handleToggleRequests.pending ? 'Sending...' : 'Send Feedback'}
          </Button>
        }
        cancelOrResetButton={closeButton}
      >
        <div className={cn('grid', formGapClasses)}>
          <p>
            Please, let us know how you like the new Design and Menu. Thank you.
          </p>

          <Textarea
            name="feedback"
            label="What works well? What would you like to improve?"
            placeholder={
              user
                ? 'Write your feedback here...'
                : "Write your feedback here... You're not logged in. If you want to be contacted, please leave your email inside the feedback."
            }
            stacked
          />
        </div>
      </Form>
    </Modal>
  );
};

const optOutSchema = z.object({
  feedback: z.string().nullable(),
});

const OptOutModal: FC<ModalApi> = ({ close, closeButton }) => {
  const { handleToggleRequests } = useToggleDesign();
  const user = useCognitoUser();

  useEffect(() => {
    if (!handleToggleRequests.response) return;
    close();
    // oxlint-disable-next-line exhaustive-deps
  }, [handleToggleRequests.response]);

  return (
    <Modal
      title="Are you sure you want to switch back?"
      onDismiss={close}
      size="lg"
      confirmButton={null}
      closeButton={null}
    >
      <Form
        schema={optOutSchema}
        initialValues={{ feedback: null }}
        onSubmit={(values) =>
          handleToggleRequests.execute({
            updateToggle: true,
            feedback: values.feedback,
          })
        }
        constraintViolation={null}
        submitButton={
          <Button type="submit" disabled={handleToggleRequests.pending}>
            {handleToggleRequests.pending ? 'Pending...' : 'Switch Back'}
          </Button>
        }
        cancelOrResetButton={closeButton}
      >
        <div className={cn('grid', formGapClasses)}>
          <p>Please, confirm that you want to switch back to the old design.</p>

          <Textarea
            name="feedback"
            label="What would you like to improve?"
            placeholder={
              user
                ? 'Write your feedback here...'
                : "Write your feedback here... You're not logged in. If you want to be contacted, please leave your email inside the feedback."
            }
            stacked
          />
        </div>
      </Form>
    </Modal>
  );
};

function useToggleDesign() {
  const now = useNow();
  const safeLocalStorage = useSafeLocalStorage();
  const isNewDesignActive = useIsNewDesignActive();
  const { isStorybook } = useStorybook();
  const { pathname, search, hash } = useLocation();

  const handleToggleRequests = useAxios(
    async (
      axios,
      baseConfig,
      {
        updateToggle,
        feedback,
      }: { updateToggle: boolean; feedback: string | null }
    ) => {
      // update timestamps, if needed (they might get included in some requests)
      if (updateToggle) {
        const timestamp = new Date(now).toISOString();
        if (isNewDesignActive)
          safeLocalStorage.setItem('PRISMA_NEW_DESIGN_OPT_OUT_TIME', timestamp);
        else
          safeLocalStorage.setItem('PRISMA_NEW_DESIGN_OPT_IN_TIME', timestamp);
      }

      // fetch organisation service, so we can fetch user and company details after that
      const organisationService = await axios
        .request<OrganisationServiceRoot>({
          ...baseConfig,
          url: PRISMA_CONFIG.organisationService,
          timeout: 5000,
        })
        .catch((error) => {
          const [_, report] = handleError(error, () => {});
          if (report) reportToSentry(error);
          return null;
        });

      // fetch company and user details
      const [organisationDetails, userDetails] = organisationService
        ? await Promise.all([
            axios
              .request<OrganisationDetailsResponse>({
                ...baseConfig,
                url: organisationService.data._links.getMyOrganisationDetails
                  .href,
                timeout: 5000,
              })
              .catch((error) => {
                const [_, report] = handleError(error, () => {});
                if (report) reportToSentry(error);
                return null;
              }),
            axios
              .request<MyUserDetailsResponse>({
                ...baseConfig,
                url: organisationService.data._links.getMyUserDetails.href,
                timeout: 5000,
              })
              .catch((error) => {
                const [_, report] = handleError(error, () => {});
                if (report) reportToSentry(error);
                return null;
              }),
          ])
        : [null, null];

      const tags = getSentryTags({
        isNewDesignActive,
        organisationDetails: organisationDetails?.data ?? null,
        userDetails: userDetails?.data ?? null,
        safeLocalStorage,
        feedback,
      });

      // send feedback to HubSpot, if provided
      if (feedback) {
        const portalId = '144146512';
        const formGuid =
          PRISMA_CONFIG.stage === 'prod'
            ? '4cbb2e5c-33cb-48f2-bab3-baaf4309d3d1'
            : '0b59006f-dcbf-4a2e-a22a-7a09c1fe3794';
        const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formGuid}`;
        const userLocation = `${pathname}${search}${hash}`;
        const fullFeedback = `${feedback}


---
User UUID: ${tags.userUuid}
Organisation UUID: ${tags.organisationUuid}
Opt In Time: ${tags.optInTime}
Opt Out Time: ${tags.optOutTime}
Stage: ${PRISMA_CONFIG.stage}
URL: ${userLocation}
`;
        await axios
          .request<unknown>({
            ...baseConfig,
            method: 'POST',
            url,
            timeout: 5000,
            data: {
              fields: [
                {
                  name: 'TICKET.subject',
                  value:
                    PRISMA_CONFIG.stage === 'prod'
                      ? '[PROD] New Design Feedback'
                      : '[NON-PROD] New Design Feedback',
                },
                { name: 'email', value: userDetails?.data.email ?? '(guest)' },
                { name: 'TICKET.content', value: fullFeedback },
              ],
            },
          })
          .catch((error) => {
            const [_, report] = handleError(error, () => {});
            if (report) reportToSentry(error);
            return null;
          });
      }

      return { updateToggle, feedback, tags };
    },
    { neededOnPageLoad: false }
  );

  // after the requests have been handled, update the local state and send the tracking data to Sentry
  useEffect(() => {
    if (!handleToggleRequests.response) return;
    const { updateToggle, feedback, tags } = handleToggleRequests.response;

    // while the feedback is also stored in the 'Design Toggle: Opt-Out' message,
    // it might be handy to keep track of it separately as well, because it is optional.
    // that way we can easily get _any_ feedback in Sentry that was provided.
    if (feedback)
      Sentry.captureMessage('Design Toggle: Feedback', { level: 'info', tags });

    if (updateToggle) {
      const message = isNewDesignActive
        ? 'Design Toggle: Opt-Out'
        : 'Design Toggle: Opt-In';
      Sentry.captureMessage(message, { level: 'info', tags });

      toggleNewDesign();

      // we need a full reload to support angular, but
      // as angular is not covered in storybook and storybook actually doesn't
      // support a real browser refresh, we can just drop it.
      if (isStorybook) return;
      locationReload();
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [handleToggleRequests.response]);

  return { handleToggleRequests, isNewDesignActive };
}

function getSentryTags({
  isNewDesignActive,
  userDetails,
  organisationDetails,
  safeLocalStorage,
  feedback,
}: {
  isNewDesignActive: boolean;
  userDetails: MyUserDetailsResponse | null;
  organisationDetails: OrganisationDetailsResponse | null;
  safeLocalStorage: Storage;
  feedback: string | null;
}) {
  const userUuid = userDetails?.userId ?? '(guest)';
  const organisationUuid = organisationDetails?.organisationId ?? '(guest)';

  const optInTime = safeLocalStorage.getItem('PRISMA_NEW_DESIGN_OPT_IN_TIME');
  const optOutTime = safeLocalStorage.getItem('PRISMA_NEW_DESIGN_OPT_OUT_TIME');
  const durationInMs = getDurationInMs({
    isNewDesignActive,
    optInTime,
    optOutTime,
  });
  const durationInDays = msToDays(durationInMs);

  return {
    userUuid,
    organisationUuid,
    optInTime,
    optOutTime,
    durationInMs,
    durationInDays,
    feedback,
  };
}

function getDurationInMs({
  isNewDesignActive,
  optInTime,
  optOutTime,
}: {
  isNewDesignActive: boolean;
  optInTime: string | null;
  optOutTime: string | null;
}) {
  if (!optInTime || !optOutTime) return 0;

  try {
    const optInTimestamp = new Date(optInTime).getTime();
    const optOutTimestamp = new Date(optOutTime).getTime();
    return isNewDesignActive
      ? optOutTimestamp - optInTimestamp
      : optInTimestamp - optOutTimestamp;
  } catch {
    return 0;
  }
}

function msToDays(milliseconds: number) {
  // return as string, because it looks like sentry cuts off the decimal places.
  // round to 2 decimal places to make it more readable.
  return (milliseconds / (1000 * 60 * 60 * 24)).toFixed(2);
}
